import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { BenchmarkPreview, BenchmarkView, ImageType } from '../../generated';
import { getBenchmarkV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdGet } from '../../generated';

/*
 * GET /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}, live behind the API's `custom-benchmarks` flag.
 * `toCustomBenchmark` is the one place the generated `BenchmarkView` is mapped; callers only ever see
 * `CustomBenchmark`. Tests answer this path with `tests/mocks/benchmark-handlers.ts`.
 */

/** A benchmark preview thumbnail: `url_64` and `url_360` are resizes, `url` the original; any can be `null`. */
export type BenchmarkPreviewView = BenchmarkPreview;

/** The resize a mosaic shows: 64px for inline thumbnails and picker rows, 360px for card mosaics. */
export type BenchmarkPreviewSize = 64 | 360;

/** A mosaic image: the URL to show and, when it is the resize, the original to show if the resize fails to load. */
export interface BenchmarkPreviewUrl {
  url: string;
  fallbackUrl?: string;
}

/**
 * The mosaic URLs for a benchmark's previews: the `size` resize (360 falls back to 64 when absent), falling back to
 * the original. The API signs the resizes before they exist (they are made when the benchmark is first scored), so a
 * set that was never scored gets resize URLs that fail, and the original stands in for them. A preview with no URL is
 * left out.
 */
export function toBenchmarkPreviewUrls(
  previews: BenchmarkPreviewView[],
  size: BenchmarkPreviewSize = 64,
): BenchmarkPreviewUrl[] {
  return previews.flatMap(({ url_360, url_64, url }) => {
    const resize = size === 360 ? (url_360 ?? url_64) : url_64;
    if (resize) return [url && url !== resize ? { url: resize, fallbackUrl: url } : { url: resize }];
    return url ? [{ url }] : [];
  });
}

/** A custom benchmark, mapped for the UI. */
export interface CustomBenchmark {
  id: string;
  name: string;
  description: string | null;
  /** Hero or Carousel, set at creation and locked afterwards. */
  scope: ImageType;
  imageCount: number;
  /** Images needed before collections can be scored against the benchmark. */
  minimumImages: number;
  /** Images the benchmark should have for stable scores. */
  recommendedImages: number;
  /** Collections scored against the benchmark; `undefined` until the API reports it. Not used yet. */
  collectionCount?: number;
  /** Thumbnail URLs for the mosaic, oldest image first: the 64px resize, falling back to the original. */
  previewImageUrls: BenchmarkPreviewUrl[];
  createdAt: string;
  updatedAt: string;
}

export function toCustomBenchmark(response: BenchmarkView): CustomBenchmark {
  return {
    id: response.id,
    name: response.name ?? '',
    description: response.description,
    // The spec types `scope` as a plain string; the API only stores `CustomBenchmarkScope`, the same two values.
    scope: response.scope as ImageType,
    imageCount: response.image_count,
    minimumImages: response.minimum_images,
    recommendedImages: response.recommended_images,
    collectionCount: response.collection_count,
    previewImageUrls: toBenchmarkPreviewUrls(response.previews),
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

export interface GetBenchmarkQueryVariables {
  workspace_id: string;
  benchmark_id: string;
}

export type GetBenchmarkQuery = CustomBenchmark;

async function getBenchmark({ workspace_id, benchmark_id }: GetBenchmarkQueryVariables) {
  const { data } = await getBenchmarkV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdGet({
    path: { workspace_id, benchmark_id },
    throwOnError: true,
  });
  return toCustomBenchmark(data);
}

export const useGetBenchmarkQuery = <TData = GetBenchmarkQuery, TError = unknown>(
  variables: GetBenchmarkQueryVariables,
  options?: Omit<UseQueryOptions<GetBenchmarkQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetBenchmarkQuery, TError, TData>({
    queryKey: useGetBenchmarkQuery.getKey(variables),
    queryFn: () => getBenchmark(variables),
    ...options,
  });
};

useGetBenchmarkQuery.getKey = ({ workspace_id, benchmark_id }: GetBenchmarkQueryVariables) => [
  'GetBenchmark',
  { workspace_id, benchmark_id },
];

useGetBenchmarkQuery.fetcher = (variables: GetBenchmarkQueryVariables) => () => getBenchmark(variables);
